import React from "react";
import { Alert, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { AddFolder } from "./modals/AddFolder";
import { useDocumentState, useDocumentPath, useDocumentData, useDocumentActions } from "../../../hooks";
import {
  DocumentToolbar,
  DocumentBreadcrumb,
  DocumentTileView,
  DocumentListView,
} from "./";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

type Props = {
  baseFolder?: boolean;
  defaultFolder?: string;
};

const Documents: React.FC<Props> = ({
  baseFolder = false,
  defaultFolder = "/",
}) => {
  const params = useParams();
  const taskId = params.id;
  
  const {
    currentPath,
    setCurrentPath,
    currentFolder,
    setCurrentFolder,
    folders,
    setFolders,
    documents,
    setDocuments,
    alertMessage,
    setAlertMessage,
    hasParentFolder,
    setHasParentFolder,
    showAddFolder,
    setShowAddFolder,
    isLoading,
    setIsLoading,
    pathFolders,
    setPathFolders,
    selectedView,
    setSelectedView,
    removeActionActive,
    setRemoveActionActive,
  } = useDocumentState(defaultFolder);

  // Initialize hooks
  const { handleSelectFolder, onUpFolder } = useDocumentPath(
    taskId,
    currentPath,
    defaultFolder,
    setPathFolders,
    setCurrentFolder,
    setAlertMessage
  );

  const { refresh } = useDocumentData(
    baseFolder,
    taskId,
    currentFolder,
    setFolders,
    setDocuments,
    setIsLoading,
    setAlertMessage,
    setHasParentFolder
  );

  const {
    handleFileSelect,
    handleDownloadDocument,
    handleRemoveDocument,
    handleRemoveFolder,
    handleFolderAdded,
  } = useDocumentActions(
    currentFolder,
    baseFolder,
    taskId,
    refresh,
    setAlertMessage,
    setRemoveActionActive
  );

  return (
    <>
      <Stack direction="vertical" gap={3}>
        <Alert variant="danger" className="mb-0">
          <FontAwesomeIcon
            className="me-2"
            icon={faExclamationTriangle}
            size="lg"
          />{" "}
          {alertMessage}
        </Alert>

        <DocumentToolbar
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          onAddFolder={() => setShowAddFolder(true)}
          onUpFolder={() => onUpFolder(currentPath, setCurrentPath)}
          onRefresh={refresh}
          hasParentFolder={hasParentFolder}
          currentPath={currentPath}
          defaultFolder={defaultFolder}
          isLoading={isLoading}
        />

        <DocumentBreadcrumb
          pathFolders={pathFolders}
          setCurrentPath={setCurrentPath}
        />

        {selectedView === "tile" && (
          <DocumentTileView
            folders={folders}
            documents={documents}
            removeActionActive={removeActionActive}
            onFolderClick={(folder) => handleSelectFolder(folder, currentPath, setCurrentPath)}
            onDocumentClick={handleDownloadDocument}
            onRemoveFolder={handleRemoveFolder}
            onRemoveDocument={handleRemoveDocument}
            onAddFile={handleFileSelect}
            onToggleRemove={() => setRemoveActionActive(!removeActionActive)}
          />
        )}

        {selectedView === "list" && (
          <DocumentListView
            folders={folders}
            documents={documents}
            removeActionActive={removeActionActive}
            onFolderClick={(folder) => handleSelectFolder(folder, currentPath, setCurrentPath)}
            onDocumentClick={handleDownloadDocument}
            onRemoveFolder={handleRemoveFolder}
            onRemoveDocument={handleRemoveDocument}
            onAddFile={handleFileSelect}
            onToggleRemove={() => setRemoveActionActive(!removeActionActive)}
          />
        )}
      </Stack>
      <AddFolder
        parentFolderId={currentFolder?.id}
        show={showAddFolder}
        onClose={(added) => handleFolderAdded(added, setShowAddFolder)}
      />
    </>
  );
};

export { Documents };

